import { Icon } from '@/design-system';
import { useTheme } from '@/lib/theme';
import { clsx } from 'clsx';

/**
 * Compact dark/light mode toggle. Reads + flips the persisted Zustand store
 * directly; the active theme is applied to <html data-theme> by
 * `useThemeEffect()` mounted in main.tsx, so we don't need any side effects
 * here beyond calling `toggle`.
 *
 * Used in two places:
 *   - AppShell mobile/desktop chrome (next to the language pill)
 *   - Landing navbar (top-right, public/unauthenticated)
 *
 * Renders as a `ui-pill`. The label collapses to icon-only on narrow viewports
 * so it fits next to other pill controls without wrapping.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const theme = useTheme((s) => s.theme);
  const toggle = useTheme((s) => s.toggle);
  const isDark = theme === 'alkisen-dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'}
      title={isDark ? 'Lyst tema' : 'Mørkt tema'}
      className={clsx('ui-pill', className)}
    >
      <Icon name={isDark ? 'sun' : 'moon'} size={12} />
      <span className="hidden sm:inline">{isDark ? 'Lys' : 'Mørk'}</span>
    </button>
  );
}
